import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { LanguageProvider } from '@george-labs.com/design-system';

import type { Language } from '@luna/i18n';
import { getCurrentLanguage } from '@luna/i18n';
import type { Locale } from '@luna/date';
import { dangerouslySetCurrentLocale, getCurrentLocale } from '@luna/date';
import { logger } from '@luna/logger';
import { getFeatureConfig } from '@luna/feature-config';

import { getGdsTranslations } from './Service';

const LANG_EN: Language = 'en';
const LANG_EN_GB_VARIANT = 'en-GB';
const LANG_CI_MODE = 'cimode';

function getLanguageVariant(): Language | 'en-GB' | 'cimode' {
    const lang = getCurrentLanguage({ allowCimode: true });
    if (lang === LANG_EN) {
        return LANG_EN_GB_VARIANT;
    }
    return lang;
}

interface Props {
    children: ReactNode;
}

export function dynamicallyImportLocale(lang: string): Promise<Locale> {
    // supported in vite
    const isGlobImportSupported = import.meta.env?.IS_VITE === 'true';

    if (isGlobImportSupported) {
        const modules = import.meta.glob<{ default: Locale }>(
            '../../../../../node_modules/date-fns/esm/locale/*/index.js',
        );

        const name = `../../../../../node_modules/date-fns/esm/locale/${lang}/index.js`;

        if (modules[name]) {
            return modules[name]().then((m) => m.default);
        }

        return modules['../../../../../node_modules/date-fns/esm/locale/en-GB/index.js']().then(
            (m) => m.default,
        );
    }

    return import(/* @vite-ignore */ `date-fns/locale/${lang}/index.js`).then((mod) => mod.default);
}

export const GdsLanguageProvider: FC<Props> = ({ children }) => {
    const [locale, setLocale] = useState<Locale | string | undefined>(getCurrentLocale());

    const lang = getLanguageVariant();
    const bankTimeZone = getFeatureConfig((c) => c.base.defaultTimezone);

    const isLangEnglishOrNotDefined = !lang || lang === LANG_EN_GB_VARIANT || lang === LANG_CI_MODE;

    useEffect(() => {
        // we don't need to import anything if lang is not defined or its english
        if (isLangEnglishOrNotDefined) {
            return;
        }
        dynamicallyImportLocale(lang)
            .then((l) => {
                setLocale(l);
                dangerouslySetCurrentLocale(l);
            })
            .catch((err) => {
                logger.error('Loading language failed. Fall-backing to EN.', err);
                setLocale(LANG_EN);
            });
    }, [isLangEnglishOrNotDefined, lang]);

    if (isLangEnglishOrNotDefined) {
        return (
            <LanguageProvider
                timeZone={bankTimeZone}
                locale={LANG_EN}
                strings={getGdsTranslations()}
            >
                {children}
            </LanguageProvider>
        );
    }

    if (!locale) {
        return null;
    }

    return (
        <LanguageProvider timeZone={bankTimeZone} locale={locale} strings={getGdsTranslations()}>
            {children}
        </LanguageProvider>
    );
};
